<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="
          d-flex
          justify-content-between
          align-items-center
          content-sidebar-header
          px-2
          py-1
        "
      >
        <h5 class="mb-0 text-capitalize">
          {{ isAdd ? $t("action_plan.documentActionPlan.addDocument") :$t("centers.edit_center") }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
            <!-- File Upload -->
            <ValidationProvider
              v-if="isAdd"
              #default="validationContext"
              name="file"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('action_plan.documentActionPlan.document')"
                label-for="document"
              >
                <b-form-file
                  id="document"
                  :state="getValidationState(validationContext)"
                  accept=".pdf"
                  v-model="file"
                  @change="onFileChange"
                  name="file"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>
          <!-- Name -->
          <validation-provider
            #default="validationContext"
            name="name"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('centers._name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Description -->
          <validation-provider
            #default="validationContext"
            name="description"
            rules="required"
          >
            <b-form-group       class="text-capitalize"
              :label="$t('centers.description')" label-for="description">
              <b-form-input
                id="description"
                v-model="itemData.description"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
          <validation-provider
            #default="validationContext"
            name="tenant"
            rules="required"
          >
            <b-form-group
            label="Tenant"
            label-for="tenant_id"
            :state="getValidationState(validationContext)"
            >
            <v-select
                data-cy="tenant-sidenav"
                v-model="itemData.tenant_id"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="tenantOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="tenant_id"
              />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              data-cy="primary-button"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") :$t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
             {{$t('actions.cancel')}}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import { extend } from 'vee-validate';
import Ripple from "vue-ripple-directive";
import store from "@/store";
extend('file', () => {
  return {
    validate: (value) => {
      return value instanceof FileList && value.length > 0;
    },
    message: 'A file is required.'
  };
});
extend('fileType', {
  validate: (value, [type]) => {
    if (!value || !(value instanceof FileList)) return false;
    const file = value[0];
    return file.type === type;
  },
  message: 'The uploaded file must be a PDF.',
});



export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    tenantOptions: {
      type: Array,
      required: true,
    },

    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const file = ref(null);
    const isFileSelected = ref(false);

    const blankData = {
      name: "",
      description: "",
      tenant: null,
      file: null
    };

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      file.value = null;
    };

    const onSubmit = () => {
      let dispatch;
      console.log(itemData.value);

      const structuredData = {
        name: itemData.value.name,
        description: itemData.value.description,
        id_tenant: itemData.value.tenant_id,
        file: itemData.value.document,
      };
      const formData = new FormData();
      formData.append("name", structuredData.name);
      formData.append("id_tenant", structuredData.id_tenant);
      formData.append("file", structuredData.file);
      formData.append("description", structuredData.description);

      const uploadingToastId = toast({
        component: ToastificationContent,
        props: {
          title: "Uploading",
          icon: "UploadIcon",
          variant: "info",
          timeout: false,
          closeOnClick: false,
        },
      });

      if (props.isAdd) {
        dispatch = store.dispatch("action_plan_document/add", formData);
      } else {
        const req = {
          id: itemData.value.id,
          params: {
            name: itemData.value.name,
            description: itemData.value.description,
            id_tenant: itemData.value.tenant_id,
            file: itemData.value.document,
          },
        };

        dispatch = store.dispatch("action_plan_document/edit", req);
      }

      dispatch
        .then((response) => {
          if (response.success) {

            toast.dismiss(uploadingToastId);
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
                timeout: false,
                closeOnClick: false,
              },
            }
            );
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast.dismiss(uploadingToastId);
            toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: "AlertTriangleIcon",
                  variant: "error",
                  timeout: false,
                  closeOnClick: false,
                },
              });
          }
        })
        .catch((response) => {
           toast.dismiss(uploadingToastId);
           toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "error",
                timeout: false,
                closeOnClick: false,
              },
            });
        });
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = props.itemEdit;
      }
    };


    const onFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        itemData.value.document = file;
      }
    };

    return {
      itemData,
      onSubmit,

      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      onFileChange,
      isFileSelected,
      file,
    };
  },
};
</script>
